import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import Column from './Column';
import Stack from './Stack';

const DIFFICULTIES = [
  { id: 'easy', label: 'Easy' },
  { id: 'medium', label: 'Medium' },
  { id: 'hard', label: 'Hard' },
  { id: 'expert', label: 'Expert' }
];

const getTileValue = (tile) => (typeof tile === 'object' && tile !== null ? tile.value : tile);
const getTileColor = (tile) =>
  typeof tile === 'object' && tile !== null && tile.color ? tile.color : 'yellow';

const Board = ({
  columns,
  stacks,
  currentTile,
  remaining,
  startTime,
  onDrawTile,
  onPlaceTile,
  onStartGame,
  difficulty,
  onDifficultyChange,
  aiScore,
  modeLabel,
  autoDealMode,
  gameStarted,
  autoMoveEvent,
  seed
}) => {
  const [elapsed, setElapsed] = useState('00:00');
  const [dragActive, setDragActive] = useState(false);
  const [touchGhost, setTouchGhost] = useState(null);
  const [confettiPieces, setConfettiPieces] = useState([]);
  const [autoMoveBanner, setAutoMoveBanner] = useState('');

  const isSoloAi = modeLabel === 'solo-ai';

  useEffect(() => {
    let interval;
    if (startTime) {
      interval = setInterval(() => {
        const diff = Date.now() - startTime;
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setElapsed(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [startTime]);

  const score = stacks.reduce((sum, stack) => {
    if (stack.length) return sum + getTileValue(stack[stack.length - 1]);
    return sum;
  }, 0);

  useEffect(() => {
    if (!autoMoveEvent?.id) return undefined;
    const count = Math.max(1, Number(autoMoveEvent.count) || 1);
    setAutoMoveBanner(`Auto-stacked ${count} tile${count > 1 ? 's' : ''}`);
    const pieces = Array.from({ length: 24 }).map((_, idx) => ({
      id: `${autoMoveEvent.id}-${idx}`,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 0.2}s`,
      duration: `${0.8 + Math.random() * 0.6}s`,
      rotate: `${Math.random() * 360}deg`
    }));
    setConfettiPieces(pieces);

    const clearTimer = setTimeout(() => {
      setConfettiPieces([]);
      setAutoMoveBanner('');
    }, 1400);
    return () => clearTimeout(clearTimer);
  }, [autoMoveEvent]);

  function parseDropZone(target) {
    const zone = target?.closest?.('[data-drop-zone="true"]');
    if (!zone) return null;
    const type = zone.dataset.dropType;
    const index = Number(zone.dataset.dropIndex);
    if (!type || Number.isNaN(index)) return null;
    return { type, index };
  }

  function handleDrop(target) {
    const zone = parseDropZone(target);
    if (!zone || currentTile === null) return;
    onPlaceTile(zone.type, zone.index);
  }

  function handleDragStart(event) {
    if (currentTile === null) return;
    try {
      event.dataTransfer.setData('text/plain', String(getTileValue(currentTile)));
      event.dataTransfer.effectAllowed = 'move';
    } catch (_err) {
      // some browsers throw on setData for non-standard types; ignore
    }
    setDragActive(true);
  }

  function handleTouchStart(event) {
    if (currentTile === null) return;
    const touch = event.touches[0];
    setDragActive(true);
    setTouchGhost({ x: touch.clientX, y: touch.clientY, value: currentTile });
  }

  function handleTouchMove(event) {
    if (!dragActive) return;
    const touch = event.touches[0];
    setTouchGhost((prev) => ({ ...(prev || {}), x: touch.clientX, y: touch.clientY, value: currentTile }));
    event.preventDefault();
  }

  function handleTouchEnd(event) {
    if (!dragActive) return;
    const touch = event.changedTouches[0];
    const dropTarget = document.elementFromPoint(touch.clientX, touch.clientY);
    handleDrop(dropTarget);
    setDragActive(false);
    setTouchGhost(null);
  }

  const dropProps = {
    onDropTile: (event) => {
      event.preventDefault();
      handleDrop(event.target);
      setDragActive(false);
    },
    onDragOverTile: (event) => event.preventDefault(),
    dropActive: dragActive
  };

  const waitingToStart = autoDealMode && !gameStarted;
  const tileKey = currentTile ? currentTile.id ?? `${getTileValue(currentTile)}-${remaining}` : 'none';

  return (
    <div className="board-shell">
      <div className="board-celebration-layer" aria-hidden="true">
        {confettiPieces.map((piece) => (
          <span
            key={piece.id}
            className="confetti-piece"
            style={{
              left: piece.left,
              animationDelay: piece.delay,
              animationDuration: piece.duration,
              transform: `rotate(${piece.rotate})`
            }}
          />
        ))}
      </div>
      {autoMoveBanner && (
        <div className="auto-move-banner" role="status">
          ✦ {autoMoveBanner}
        </div>
      )}

      <div className={`hud-strip ${isSoloAi ? '' : 'three'}`} aria-label="Game status">
        <div className="hud-stat accent">
          <span className="hud-label">Your score</span>
          <span className="hud-value">{score}</span>
        </div>
        {isSoloAi && (
          <div className="hud-stat">
            <span className="hud-label">Computer</span>
            <span className="hud-value">{aiScore}</span>
          </div>
        )}
        <div className="hud-stat">
          <span className="hud-label">Time</span>
          <span className="hud-value">{elapsed}</span>
        </div>
        <div className="hud-stat">
          <span className="hud-label">Tiles left</span>
          <span className="hud-value">{remaining}</span>
        </div>
      </div>

      <div className="game-layout">
        <div className="table-felt">
          <p className="zone-label">Foundations · build 1 through 13</p>
          <div className="stack-row">
            {stacks.map((stack, idx) => (
              <Stack key={idx} values={stack} onClick={() => onPlaceTile('stack', idx)} dropIndex={idx} {...dropProps} />
            ))}
          </div>
          <p className="zone-label">Columns · hold tiles for later</p>
          <div className="column-row">
            {columns.map((col, idx) => (
              <Column key={idx} values={col} onClick={() => onPlaceTile('column', idx)} dropIndex={idx} {...dropProps} />
            ))}
          </div>
        </div>

        <aside className="dock-draw">
          <div className="surface-panel dark dock-panel">
            {waitingToStart ? (
              <div className="start-dock">
                <p className="dock-title">Ready</p>
                <Button size="lg" onClick={onStartGame}>
                  Deal the Cards
                </Button>
                <p>Tiles deal one at a time. Drag each onto a foundation or a column.</p>
              </div>
            ) : (
              <>
                <p className="dock-title">Current tile</p>
                <div className="draw-dock">
                  {currentTile === null ? (
                    <div className="draw-empty">{autoDealMode ? 'Dealing…' : 'Draw a tile'}</div>
                  ) : (
                    <div
                      key={tileKey}
                      className={`counter large counter-${getTileColor(currentTile)}`}
                      draggable
                      onDragStart={handleDragStart}
                      onDragEnd={() => setDragActive(false)}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      aria-label={`Current tile ${getTileValue(currentTile)}. Drag onto the table.`}
                    >
                      {getTileValue(currentTile)}
                    </div>
                  )}
                  <div>
                    {!autoDealMode && (
                      <Button className="mb-2" onClick={onDrawTile} disabled={currentTile !== null}>
                        Draw Tile
                      </Button>
                    )}
                    <p className="draw-caption">
                      {currentTile === null
                        ? 'Your next tile appears here.'
                        : 'Drag it onto the table, or tap a spot to place it.'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </aside>

        <aside className="dock-settings">
          {isSoloAi && (
            <div className="surface-panel dark dock-panel">
              <p className="dock-title">Computer difficulty</p>
              <div className="segmented" role="radiogroup" aria-label="Computer difficulty">
                {DIFFICULTIES.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={difficulty === option.id}
                    className={`segment ${difficulty === option.id ? 'active' : ''}`}
                    onClick={() => onDifficultyChange(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {seed && <p className="seed-note">Deck seed {seed}</p>}
        </aside>
      </div>

      {touchGhost && (
        <div
          className={`touch-drag-ghost counter-${getTileColor(touchGhost.value)}`}
          style={{ left: touchGhost.x, top: touchGhost.y }}
        >
          {getTileValue(touchGhost.value)}
        </div>
      )}
    </div>
  );
};

export default Board;
